import React from "react";
import { Badge, Callout, Card, PageHeader, Select } from "../components/DesignSystem";
import type { DayOfContext, DayOfView, ID } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState } from "./common";

export function DayOfScreen() {
  const { loading, error, service } = usePlannerData();
  const [context, setContext] = React.useState<DayOfContext | null>(null);
  const [eventId, setEventId] = React.useState<ID | "">("");
  const [view, setView] = React.useState<DayOfView | null>(null);
  const [viewLoading, setViewLoading] = React.useState(false);
  const [viewError, setViewError] = React.useState<string | null>(null);

  React.useEffect(() => {
    void (async () => {
      const ctx = await service.dayOf.getContext();
      setContext(ctx);
      setEventId(ctx.todayEventId || "");
    })();
  }, [service]);

  const load = React.useCallback(async (id: ID) => {
    setViewLoading(true);
    setViewError(null);
    try {
      setView(await service.dayOf.getForEvent(id));
    } catch (err) {
      setViewError(errorMessage(err));
    } finally {
      setViewLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    if (eventId) void load(eventId);
  }, [eventId, load]);

  if (loading || !context) return <div className="pob-pad"><LoadingState label="Loading wedding day mode" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  return (
    <div>
      <PageHeader
        eyebrow="Today"
        title="Wedding day"
        meta={context.todayEventId ? [{ icon: "party-popper", label: "It's the big day!" }] : []}
      />
      <div className="pob-pad">
        <Card padding="var(--space-4)" style={{ marginBottom: "var(--space-4)" }}>
          <Select value={eventId} placeholder="Select a celebration" onChange={(e) => setEventId(e.target.value)} options={context.events.map((e) => ({ value: e.id, label: e.name }))} />
        </Card>

        {!eventId && <Callout tone="neutral" title="Select a celebration">Wedding Day Mode always shows one celebration at a time.</Callout>}
        {eventId && viewLoading && !view && <LoadingState label="Loading" />}
        {viewError && <ErrorState message={viewError} retry={() => eventId && load(eventId)} />}

        {view && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            {view.warnings.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                {view.warnings.map((w, i) => <Callout key={i} tone="error" title={w.text} />)}
              </div>
            )}

            <Card eyebrow={view.event.name} title={[view.event.venue, view.event.time].filter(Boolean).join(" · ") || "Today's celebration"}>
              <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>Run sheet, contacts and transport for this celebration only — nothing else you don't need right now.</span>
            </Card>

            <Card eyebrow="Run sheet" title="Timeline">
              {view.runSheet.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No run sheet items yet.</span>}
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                {view.runSheet.map((item) => (
                  <div key={item.id} style={{ display: "flex", gap: "var(--space-3)", alignItems: "baseline" }}>
                    <span style={{ font: "var(--type-label)", width: 56 }}>{item.time || "—"}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ font: "var(--type-body)" }}>{item.activity}</span>
                      {(item.ownerLabel || item.vendorName || item.venueName) && (
                        <span style={{ display: "block", font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{[item.ownerLabel, item.vendorName, item.venueName].filter(Boolean).join(" · ")}</span>
                      )}
                    </div>
                    <Badge tone={item.status === "done" ? "success" : item.status === "delayed" ? "error" : "neutral"}>{item.status.replace(/_/g, " ")}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            <Card eyebrow="Contacts" title="Vendors & venue">
              {view.contacts.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No contacts linked to this celebration.</span>}
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                {view.contacts.map((contact) => (
                  <div key={contact.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ font: "var(--type-label)", display: "block" }}>{contact.name}</span>
                      <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{contact.category}</span>
                    </div>
                    {contact.phone && <a href={`tel:${contact.phone}`} style={{ font: "var(--type-label)", color: "var(--text-accent)" }}>{contact.phone}</a>}
                  </div>
                ))}
              </div>
            </Card>

            {view.venues.length > 0 && (
              <Card eyebrow="Venue" title="Where">
                {view.venues.map((venue) => (
                  <div key={venue.id} style={{ marginBottom: "var(--space-2)" }}>
                    <span style={{ font: "var(--type-label)", display: "block" }}>{venue.name}</span>
                    <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{venue.address}</span>
                    {venue.access && <span style={{ display: "block", font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{venue.access}</span>}
                  </div>
                ))}
              </Card>
            )}

            <Card eyebrow="Transport" title={`${view.transport.requiredSeats} seats needed · ${view.transport.availableSeats} available`}>
              {view.transport.routes.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No transport routes for this celebration.</span>}
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                {view.transport.routes.map((route) => (
                  <div key={route.id} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ font: "var(--type-body)" }}>{route.name} ({route.fromLabel} → {route.toLabel}){route.time ? ` · ${route.time}` : ""}</span>
                    <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{route.seated}/{route.capacity}</span>
                  </div>
                ))}
              </div>
            </Card>

            {view.outstandingTasks.length > 0 && (
              <Card eyebrow="Outstanding tasks" title={`${view.outstandingTasks.length} not yet done`}>
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                  {view.outstandingTasks.map((task) => (
                    <div key={task.id} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ font: "var(--type-body)" }}>{task.title}</span>
                      <Badge tone={task.priority === "high" ? "error" : "neutral"}>{task.priority}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {view.documents.length > 0 && (
              <Card eyebrow="Documents" title="Linked to this celebration">
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
                  {view.documents.map((doc) => (
                    <span key={doc.id} style={{ font: "var(--type-body)" }}>{doc.name} <span style={{ color: "var(--text-tertiary)" }}>({doc.type})</span></span>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
